import './claim-entity.detail.notes.scss';

import { AttachmentEdges, NoteUI } from './claim-note-modal';
import { Avatar, Box, Chip, Typography } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import {
  NoteCategoryType,
  NoteNode,
  useGetCategoriesForNote,
  useGetNoteForClaim,
} from '@converge/graphql';
import { capitalizeFirstLetter, displayFormatDate } from '@converge/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { CircularLoader } from '../../../presentation/components/circular-loader';
import { ClaimEntityPageContainer } from './claim-entity.page.container';
import { ClaimNoteModal } from './claim-note-modal';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { UnifiedDeleteContactModal } from './unified-delete-contact-modal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LockIcon from '@mui/icons-material/Lock';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { NoteSearch } from './note-search';
import React from 'react';
import { styled } from '@mui/material/styles';
import { highlightText } from '../../../presentation/utils/highlight-text';
import { useAuthContext } from '../../auth';
import { useOutletContext } from 'react-router-dom';

marked.setOptions({
  gfm: true, // Enable GitHub Flavored Markdown features including strikethrough
});

interface CategoryOption {
  value: string;
  label: string;
  selected: boolean;
}
export interface NoteEntityIProps {
  litigation?: boolean | null | undefined;
  registerPageRefetch?: (refetchFn: () => void) => void;
}

// Styled components
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
  marginBottom: '16px',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} sx={{ flexDirection: 'row' }} />
))(() => ({
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: '16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
}));

export const ClaimEntityDetailNote = () => {
  const { registerPageRefetch } = useOutletContext<NoteEntityIProps>();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState<NoteUI | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  const { categories: categoriesList, loading: categoriesLoading } =
    useGetCategoriesForNote();
  const { user } = useAuthContext();
  const currentUser = user?.user.username;
  const [expandedAttachmentRows, setExpandedAttachmentRows] = useState<
    Record<string, boolean>
  >({});
  const [isACESort, setIsACESort] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categories, setCategories] = useState<CategoryOption[]>([]);

  // Debounce search to avoid rapid server re-fetches for the text-search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Initialize categories only once — do not overwrite user selections on re-render
  useEffect(() => {
    if (
      categoriesList &&
      categoriesList.length > 0 &&
      categories.length === 0
    ) {
      setCategories(categoriesList as CategoryOption[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesList]);

  // Get claim UUID from URL parameters
  const extractClaimUuidFromUrl = (
    url: string = window.location.pathname
  ): string => {
    const match = url.match(/\/claims\/([^/]+)/);
    return match ? match[1] : '';
  };

  const uuid = extractClaimUuidFromUrl();
  // Get selected categories for filtering
  const selectedCategories = categories
    .filter((cat) => cat.selected)
    .map((cat) => cat.value as NoteCategoryType);

  const categoryFilters =
    selectedCategories.length > 0 ? { categories: selectedCategories } : {};
  const sortParam = isACESort ? undefined : { key: 'updatedAt', order: 'ASC' };

  // Primary query: normal pagination — auto-loads all pages when searching (for name filtering)
  const {
    notes: loadedNotes,
    loading,
    hasNextPage,
    loadMore,
    refetch: refetchLoaded,
  } = useGetNoteForClaim({
    claimUuid: uuid || '',
    sort: sortParam,
    additionalFilters: categoryFilters,
  });

  // Text-search query: server-side matchStr finds unloaded notes by content
  const {
    notes: textMatchNotes,
    loading: textSearchLoading,
    refetch: refetchTextSearch,
  } = useGetNoteForClaim({
    claimUuid: uuid || '',
    sort: sortParam,
    additionalFilters: { ...categoryFilters, matchStr: debouncedSearch },
    skip: !debouncedSearch,
  });

  // Merge: loadedNotes filtered by name/text + server text-match results, deduplicated
  const notes = useMemo(() => {
    if (!searchText) return loadedNotes;

    const q = searchText.toLowerCase();
    const nameOrTextMatches = loadedNotes.filter((n) => {
      const firstName = n.user?.node?.firstName?.toLowerCase() || '';
      const lastName = n.user?.node?.lastName?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.trim();
      return (
        n.note?.toLowerCase().includes(q) ||
        firstName.includes(q) ||
        lastName.includes(q) ||
        fullName.includes(q)
      );
    });

    const combined = [...nameOrTextMatches, ...textMatchNotes];
    const seen = new Set<string>();
    return combined.filter((n) => {
      const key = n.uuid || '';
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [searchText, loadedNotes, textMatchNotes]);

  const hasNotes = notes.length > 0;

  const refetch = useCallback(() => {
    refetchLoaded();
    if (debouncedSearch) {
      refetchTextSearch();
    }
  }, [refetchLoaded, refetchTextSearch, debouncedSearch]);

  const isScrollLoadingRef = useRef(false);

  useEffect(() => {
    if (!loading) {
      isScrollLoadingRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target || isScrollLoadingRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = target;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        isScrollLoadingRef.current = true;
        loadMore();
      }
    };

    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [loadMore]);

  // When searching, auto-load all pages so name filtering covers every note
  useEffect(() => {
    if (debouncedSearch && hasNextPage && !loading) {
      loadMore();
    }
  }, [debouncedSearch, hasNextPage, loading, loadMore]);

  const wasModalOpenRef = useRef(false);

  // Only refetch when modal closes, not when it opens
  useEffect(() => {
    const justClosed = wasModalOpenRef.current && !isEditModalOpen;
    wasModalOpenRef.current = isEditModalOpen;
    if (uuid && justClosed) {
      refetch();
    }
  }, [uuid, isEditModalOpen, refetch]);

  // Register this page's refetch function with the parent
  useEffect(() => {
    if (registerPageRefetch && refetch) {
      registerPageRefetch(refetch);
    }
  }, [registerPageRefetch, refetch]);

  const toggleCategory = useCallback(
    (id: string) => {
      // First update the local state
      const updatedCategories = categories.map((category) =>
        category.value === id
          ? { ...category, selected: !category.selected }
          : category
      );

      setCategories(updatedCategories);
    },
    [categories]
  );

  const handleOthersAccordion = (
    noteId: string,
    _event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setExpandedAttachmentRows((prev) => ({
      ...prev,
      [noteId]: newExpanded,
    }));
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentNote(null);
  };

  const handleOpenDeleteModal = (noteUuid: string) => {
    setNoteToDelete(noteUuid);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setNoteToDelete(null);
  };

  return (
    <>
      <Box className="note-container">
        <ClaimEntityPageContainer
          title={'Notes'}
          titleChildren={
            <Box style={{ backgroundColor: 'white' }}>
              <>
                <Box>
                  <NoteSearch onSearch={setSearchText} />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    margin: '40px 20px 30px 0px',
                    paddingBottom: '20px',
                    width: '80%',
                  }}
                >
                  <Box sx={{ width: '75%' }}>
                    {categories.map((category) => (
                      <Chip
                        key={category.value}
                        label={formatCategoryLabel(category.label)}
                        onClick={() => toggleCategory(category.value)}
                        variant={category.selected ? 'filled' : 'outlined'}
                      />
                    ))}
                  </Box>
                  <Box
                    className="sort-by-date"
                    sx={{ width: '25%' }}
                    onClick={() => setIsACESort((prev) => !prev)}
                  >
                    <Box style={{ marginTop: '3px' }}> SORT BY DATE</Box>

                    <Box sx={{ marginBottom: '30px' }}>
                      {/* Arrow up means descending (newest first) */}
                      {isACESort ? (
                        <ArrowUpwardIcon
                          sx={{
                            marginLeft: '8px',
                          }}
                        />
                      ) : (
                        /* Arrow down means ascending (oldest first) */
                        <ArrowDownwardIcon sx={{ marginLeft: '8px' }} />
                      )}
                    </Box>
                  </Box>
                </Box>
              </>
            </Box>
          }
        >
          <Box style={{ marginTop: '180px', position: 'relative' }}>
            {(loading && !hasNotes) ||
            (textSearchLoading && !hasNotes) ||
            categoriesLoading ? (
              <CircularLoader className="circular-loader-style" />
            ) : !hasNotes ? (
              <Box
                sx={{
                  position: 'fixed',
                  top: '180px',
                  left: 0,
                  width: '100%',
                  height: 'calc(100vh - 20px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                {searchText
                  ? `No results found for "${searchText}"`
                  : 'There are no notes associated with this claim.'}
              </Box>
            ) : notes && notes.length > 0 ? (
              (() => {
                return (
                  <>
                    {notes.map((item: NoteNode, index: number) => {
                      const username = item.user?.node?.username || '';
                      const initialUser = username.slice(0, 2).toUpperCase();

                      return (
                        <React.Fragment key={item.uuid || index}>
                          <Box className="note-detail-container">
                            <Avatar className="avatar-icon note-details">
                              {initialUser}
                            </Avatar>
                            <Box
                              className="note-details"
                              style={{ fontWeight: '700', marginRight: '10px' }}
                            >
                              {highlightText(
                                capitalizeFirstLetter(
                                  item.user?.node?.firstName || ''
                                ),
                                searchText,
                                { fullNameShortCircuit: true }
                              )}{' '}
                              {highlightText(
                                capitalizeFirstLetter(
                                  item.user?.node?.lastName || ''
                                ),
                                searchText,
                                { fullNameShortCircuit: true }
                              )}
                            </Box>
                            <Box className="note-details">
                              {displayFormatDate(item?.updatedAt)}
                            </Box>
                            <Box className="note-details">
                              {item?.updatedAt &&
                                new Date(item.updatedAt).toLocaleTimeString(
                                  'en-US',
                                  {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true,
                                  }
                                )}
                            </Box>
                            <Box className="edit-icon">
                              {username === currentUser && item.editable ? (
                                <>
                                  <EditNoteIcon
                                    style={{
                                      fontSize: '24px',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                      // Convert the item to our NoteUI type
                                      setCurrentNote({
                                        uuid: item.uuid,
                                        note: item.note,
                                        categories: item.categories,
                                        createdAt: item.createdAt,
                                        updatedAt: item.updatedAt,
                                        targetUuid: item.targetUuid,
                                        targetType: item.targetType,
                                        targetName: item.targetName,
                                        editable: item.editable,
                                        label: item.label,
                                        user: item.user,
                                        // Mapping attachments from GraphQL format to NoteUI format
                                        attachments:
                                          item.attachments as AttachmentEdges,
                                      });
                                      setIsEditModalOpen(true);
                                    }}
                                  />
                                  <HighlightOffIcon
                                    style={{
                                      fontSize: '24px',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                      if (item.uuid) {
                                        handleOpenDeleteModal(item.uuid);
                                      }
                                    }}
                                  />
                                </>
                              ) : (
                                <LockIcon style={{ fontSize: '24px' }} />
                              )}
                            </Box>
                            <Box>
                              {item.categories && item.categories.length > 0 ? (
                                item.categories.map((category) => (
                                  <Chip
                                    key={category}
                                    label={formatCategoryLabel(category)}
                                    variant={'filled'}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption">
                                  No categories
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box className="note-content-style">
                            {renderNoteWithHighlight(
                              item.note || '',
                              searchText
                            )}
                          </Box>
                          {item.attachments.edges.length > 0 && (
                            <Box style={{ marginLeft: '38px' }}>
                              <Accordion
                                expanded={
                                  expandedAttachmentRows[item.uuid || ''] ||
                                  false
                                }
                                onChange={(_event, newExpanded) =>
                                  handleOthersAccordion(
                                    item.uuid || '',
                                    _event,
                                    newExpanded
                                  )
                                }
                                style={{ marginBottom: '30px' }}
                              >
                                <AccordionSummary>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {expandedAttachmentRows[item.uuid || ''] ? (
                                      <KeyboardArrowDownIcon />
                                    ) : (
                                      <KeyboardArrowRightIcon />
                                    )}
                                    <Typography
                                      variant="h5"
                                      className="attachment-node-style"
                                    >
                                      Attached Files
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails className="accordion-details-style">
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      width: '100%',
                                    }}
                                  >
                                    <Box>
                                      {item.attachments.edges.map(
                                        (attachment) => {
                                          const name =
                                            attachment.node.filePath
                                              ?.split('/')
                                              .pop() || 'note';
                                          return (
                                            <Box className="upload-name-list">
                                              {attachment.node.folder} / {''}
                                              <a
                                                href={attachment.node.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  window.open(
                                                    attachment.node.url,
                                                    '_blank'
                                                  );
                                                }}
                                              >
                                                {name}
                                              </a>
                                            </Box>
                                          );
                                        }
                                      )}
                                    </Box>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            </Box>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                );
              })()
            ) : null}
            {(loading || textSearchLoading) && hasNotes && (
              <CircularLoader className="circular-loader-style" />
            )}
          </Box>
        </ClaimEntityPageContainer>
      </Box>

      {/* Edit Note Modal */}
      {currentNote && (
        <ClaimNoteModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          onNoteSaved={() => {
            console.log('Note saved, refreshing data...');
            refetch();
          }}
          existingNote={currentNote}
        />
      )}

      {/* Delete Note Modal */}
      <UnifiedDeleteContactModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onNoteSaved={refetch}
        mode="note"
        noteData={noteToDelete ? { noteUuid: noteToDelete } : undefined}
      />
    </>
  );
};

function renderNoteWithHighlight(
  note: string,
  searchText: string
): JSX.Element {
  if (!note) {
    return <></>;
  }

  if (!searchText) {
    const renderedMarkdown = marked(note);
    return <div dangerouslySetInnerHTML={{ __html: renderedMarkdown }} />;
  }
  const renderedMarkdown = marked(note);
  const highlightedHtml = renderedMarkdown.replace(
    new RegExp(`(${searchText})`, 'gi'),
    '<span style="background-color: rgb(181, 251, 241);">$1</span>'
  );

  return <div dangerouslySetInnerHTML={{ __html: highlightedHtml }} />;
}

export function formatCategoryLabel(label: string): string {
  if (!label) return '';

  // Replace underscores with spaces
  const withSpaces = label.replace(/_/g, ' ');

  // Special case: preserve "FNOL" as is
  if (withSpaces === 'FNOL') return withSpaces;

  // If the string is all uppercase, convert to title case
  if (withSpaces === withSpaces.toUpperCase()) {
    return withSpaces
      .split(' ')
      .map((word) => {
        // Special case for FNOL
        if (word === 'FNOL') return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  // Otherwise return the original string with spaces
  return withSpaces;
}
